import { TextareaAutosize, Tooltip } from "@material-ui/core"
import useDidUpdateEffect from "hooks/useDidUpdateEffect"
import React, { useEffect, useState } from "react"
import CurrencyInput from "react-currency-input-field"
import Loader from "react-loaders"
import { Col, FormGroup, Input, Label, Row } from "reactstrap"
import api from "services/api.service"
import { userService } from "services/user.service"
import colors from "utils/colors"
import { currencyFormatter } from "utils/formatters"
import moment from "moment"

export const CostPoolEditableCell = ({
  costPoolYear,
  costPool,
  propName,
  maxYear,
  prefix = "",
  suffix = "",
  isBoldFunc = undefined,
}) => {
  const year = costPool?.bcCostPoolYears?.find(
    x => x.yearID == costPoolYear?.yearID
  )

  return (
    <div
      className={
        year?.yearID < maxYear || costPool.flowThrough == true
          ? "always-disabled"
          : ""
      }
    >
      {year != undefined && (
        <CurrencyInput
          className="form-control align-end"
          style={{
            padding: "1px",
            fontSize: "12px",
            fontWeight:
              isBoldFunc != undefined && isBoldFunc(year[propName])
                ? "bold"
                : "normal",
          }}
          value={year[propName]}
          disabled={true}
          prefix={prefix}
          suffix={suffix}
        />
      )}
    </div>
  )
}

export const getColorsByTableName = tableName => {
  if (tableName == "costTable1") {
    return { backgroundColor: "#006601", color: "white" }
  }
  if (tableName == "costTable2") {
    return { backgroundColor: "#339969", color: "white" }
  }
  if (tableName == "costTable3") {
    return { backgroundColor: "#cdfccf", color: "black" }
  }
  if (tableName == "benefitTable1") {
    return { backgroundColor: "#010362", color: "white" }
  }
  if (tableName == "benefitTable2") {
    return { backgroundColor: "#0098cd", color: "white" }
  }
  if (tableName == "benefitTable3") {
    return { backgroundColor: "#ceeaff", color: "black" }
  }
}

const ForecastSectionViewOnly = ({ alternativeId, alternative, scenario }) => {
  const currentUser = userService.getLoggedInUser()
  const [forecastSummary, setForecastSummary] = useState([])
  const [costTable1, setCostTable1] = useState([])
  const [costTable2, setCostTable2] = useState([])
  const [costTable3, setCostTable3] = useState([])
  const [costTotals, setCostTotals] = useState(null)
  const [benefitTable1, setBenefitTable1] = useState([])
  const [benefitTable2, setBenefitTable2] = useState([])
  const [benefitTable3, setBenefitTable3] = useState([])
  const [benefitTotals, setBenefitTotals] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [expenseOptions, setExpenseOptions] = useState([])
  const [fiscalYears, setFiscalYears] = useState([])
  const [showResourceEstimates, setShowResourceEstimates] = useState(false)
  const [resourceEstimateData, setResourceEstimateData] = useState([])

  const [useRates, setUseRates] = useState(false)

  useDidUpdateEffect(() => {
    loadData(true)
  }, [useRates])

  //[{ yearID}]
  const [costPoolYears, setCostPoolYears] = useState([])

  useEffect(() => {
    if (alternativeId > 0) {
      constructFiscalYears()
      loadData()
    }
  }, [alternativeId])

  const constructFiscalYears = () => {
    let years = []
    let dt = new Date()
    let dtYearStart = dt.getFullYear() - 10
    for (let i = 0; i <= 25; i++) {
      years.push(dtYearStart + i)
    }
    setFiscalYears(years)
  }

  const loadData = async toggleLoading => {
    if (toggleLoading === undefined || toggleLoading === true)
      setIsLoading(true)
    let costs = await api.getBCCostPools(alternativeId, "cost", false, useRates)
    let costWithYears = costs.find(
      x => x.bcCostPoolYears !== null && x.bcCostPoolYears.length > 0
    )
    if (costWithYears !== undefined) {
      setCostPoolYears(
        costWithYears.bcCostPoolYears.map(y => {
          return { yearID: y.yearID }
        })
      )
    }
    let ct1 = []
    let ct2 = []
    let ct3 = []
    for (let i = 0; i < costs.length; i++) {
      if (i == 0) {
        ct1.push(costs[i])
        continue
      }
      if (ct1.filter(x => x.bcCostPoolID < 0).length !== 2) {
        ct1.push(costs[i])
        continue
      } else {
        if (ct2.filter(x => x.bcCostPoolID < 0).length !== 2) {
          ct2.push(costs[i])
          continue
        } else {
          if (ct3.filter(x => x.bcCostPoolID < 0).length !== 2) {
            ct3.push(costs[i])
            continue
          }
        }
      }
    }
    setCostTable1(ct1)
    setCostTable2(ct2)
    setCostTable3(ct3)
    setCostTotals(costs[costs.length - 1])

    let benefits = await api.getBCCostPools(
      alternativeId,
      "benefit",
      false,
      useRates
    )
    let bt1 = []
    let bt2 = []
    let bt3 = []
    for (let i = 0; i < benefits.length; i++) {
      if (i == 0) {
        bt1.push(benefits[i])
        continue
      }
      if (bt1.filter(x => x.bcCostPoolID < 0).length !== 2) {
        bt1.push(benefits[i])
        continue
      } else {
        if (bt2.filter(x => x.bcCostPoolID < 0).length !== 2) {
          bt2.push(benefits[i])
          continue
        } else {
          if (bt3.filter(x => x.bcCostPoolID < 0).length !== 2) {
            bt3.push(benefits[i])
            continue
          }
        }
      }
    }
    setBenefitTable1(bt1)
    setBenefitTable2(bt2)
    setBenefitTable3(bt3)
    setBenefitTotals(benefits[benefits.length - 1])

    await loadForecastSummary()
    await loadExpenseOptions()

    let estimates = await api.getResourceFlow(currentUser.userID, alternativeId)
    setResourceEstimateData(estimates)

    setIsLoading(false)
  }

  const loadForecastSummary = async () => {
    let summary = await api.getAlternativeForecastSummary(alternativeId)
    setForecastSummary(
      summary.map(x => {
        let ret = {}
        Object.keys(x)
          .filter(
            k =>
              k.startsWith("c-") ||
              k == "backColor" ||
              k == "format" ||
              k == "bold"
          )
          .forEach(p => {
            ret[p] = x[p]
          })
        return ret
      })
    )
  }

  const loadExpenseOptions = async () => {
    let exp = await api.getList(currentUser.userID, "ExpenseType")
    setExpenseOptions(exp)
  }

  const getSetterByTableName = tableName => {
    if (tableName == "costTable1") {
      return setCostTable1
    }
    if (tableName == "costTable2") {
      return setCostTable2
    }
    if (tableName == "costTable3") {
      return setCostTable3
    }
    if (tableName == "benefitTable1") {
      return setBenefitTable1
    }
    if (tableName == "benefitTable2") {
      return setBenefitTable2
    }
    if (tableName == "benefitTable3") {
      return setBenefitTable3
    }
  }

  const getTotals = (table, propToSum) => {
    return table.reduce((acc, item) => {
      return acc + item[propToSum]
    }, 0)
  }

  const editNote = (note, list, listName) => {
    let copy = [...list]
    copy.forEach(element => {
      element.showTextarea = false
    })
    let item = copy.find(x => x.bcCostPoolID == note.bcCostPoolID)
    item.showTextarea = true
    let setter = getSetterByTableName(listName)
    setter(copy)
  }

  const getTableRowsToRender = (list, listName) => {
    return (
      <React.Fragment>
        {list.length > 0 &&
          list.map((c, i) => {
            if (i == 0 || c.bcCostPoolID > 0)
              return (
                <tr
                  key={i}
                  className={useRates ? "always-disabled" : ""}
                  style={{
                    backgroundColor:
                      i == 0
                        ? getColorsByTableName(listName).backgroundColor
                        : "inherit",
                    color:
                      i == 0 ? getColorsByTableName(listName).color : "inherit",
                  }}
                >
                  <td
                    style={{
                      verticalAlign: "middle",
                      fontWeight: i == 0 ? 600 : "inherit",
                      backgroundColor:
                        getColorsByTableName(listName).backgroundColor,
                      color: getColorsByTableName(listName).color,
                    }}
                  >
                    {/* <a
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        editCostPoolLineItem(c)
                      }}
                    > */}
                    <span
                    //   style={{ cursor: "pointer" }}
                    //   onClick={() => {
                    //     editCostPoolLineItem(c)
                    //   }}
                    >
                      {c.costPoolName}
                    </span>
                    {/* </a> */}
                  </td>
                  {costPoolYears.length > 0 &&
                    costPoolYears.map((y, idx) => (
                      <td key={idx}>
                        {i !== 0 && (
                          <CostPoolEditableCell
                            costPool={c}
                            costPoolYear={y}
                            propName="amount"
                            key={idx}
                            maxYear={maxYear}
                          />
                        )}
                      </td>
                    ))}

                  <td
                    className="align-end"
                    style={{
                      backgroundColor: i !== 0 ? "lightgray" : "transparent",
                      fontWeight: "bold",
                    }}
                  >
                    {i !== 0 && currencyFormatter.format(c.totalAmount)}
                  </td>
                  {!showResourceEstimates && (
                    <td style={{ padding: "1px" }}>
                      {i == 0 ? (
                        c.notes
                      ) : c.showTextarea ? (
                        <TextareaAutosize
                          className="form-control"
                          value={c.notes}
                          disabled={true}
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                          }}
                        >
                          <p
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: "600px",
                              marginBottom: "0px",
                            }}
                          >
                            {c.flowThrough == true
                              ? "This is a flow through cost pool. See the Resource Estimate tab. "
                              : ""}{" "}
                            {c.notes}
                          </p>
                          {c.flowThrough != true && (
                            <i
                              className="fas fa-edit"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => editNote(c, list, listName)}
                            ></i>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              )
          })}
        {list.length > 0 && (
          <tr
            style={{
              backgroundColor: getColorsByTableName(listName).backgroundColor,
              color: getColorsByTableName(listName).color,
              fontWeight: 600,
            }}
          >
            <td style={{ textAlign: "right" }}>
              {list[list.length - 1].costPoolName}
            </td>
            {costPoolYears.length > 0 &&
              costPoolYears.map((y, idx) => (
                <td key={idx} className="align-end">
                  {currencyFormatter.format(getYearTotals(list, y))}
                </td>
              ))}
            <td className="align-end" style={{ fontWeight: "bold" }}>
              {currencyFormatter.format(getTotals(list, "totalAmount"))}
            </td>
            {!showResourceEstimates && <td></td>}
          </tr>
        )}
      </React.Fragment>
    )
  }

  const getYearTotals = (table, year) => {
    return table.reduce((acc, item) => {
      const existingYear = item.bcCostPoolYears.find(
        x => x.yearID == year.yearID
      )
      return acc + (existingYear !== undefined ? existingYear.amount : 0)
    }, 0)
  }

  const maxYear = alternative?.lockYearData ? moment().year() : 0

  const hasBenefitRecordsTable1 =
    benefitTable1.find(x => x.bcCostPoolID > 0) != undefined
  const hasBenefitRecordsTable2 =
    benefitTable2.find(x => x.bcCostPoolID > 0) != undefined
  const hasBenefitRecordsTable3 =
    benefitTable3.find(x => x.bcCostPoolID > 0) != undefined

  return (
    <React.Fragment>
      {isLoading && costTable1.length == 0 && (
        <Loader
          type="line-scale-pulse-out"
          color={colors.primary}
          style={{ textAlign: "center" }}
        />
      )}
      {(!isLoading || costTable1.length > 0) && (
        <React.Fragment>
          <Row>
            <Col sm="2">
              <div>
                <Label className="form-label">Starting Fiscal Year</Label>
                <p>{alternative.fyStart}</p>
              </div>
            </Col>
            <Col sm="3">
              <div>
                <Label className="form-label">
                  Planning Horizon (in Years)
                </Label>
                <p>{alternative.numForecastYears}</p>
              </div>
            </Col>
            <Col sm="2">
              <div>
                <Label className="form-label">Expense Type</Label>
                <p>{alternative.colorOfMoney}</p>
              </div>
            </Col>
            <Col sm="5">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <div className="form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="customCheck4"
                    checked={showResourceEstimates}
                    disabled={true}
                  />
                  <Label className="form-check-label" for="customCheck4">
                    Show Resource Estimate Data
                  </Label>
                </div>
              </div>
            </Col>
          </Row>

          <Row style={{ marginTop: "20px" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div className="d-flex-row ai-center" style={{ gap: "20px" }}>
                  {/* <div className="d-flex-row"> */}
                  <FormGroup switch style={{ marginLeft: "10px" }}>
                    <Input
                      type="switch"
                      checked={alternative.useDiscount}
                      style={{
                        marginLeft: "0px",
                        backgroundColor: alternative.useDiscount
                          ? "green"
                          : "transparent",
                      }}
                      disabled={true}
                    />
                    <Label check style={{ marginLeft: "10px" }}>
                      Apply Discount Rates
                    </Label>
                  </FormGroup>
                  {alternative.useDiscount && (
                    <div
                      style={{
                        marginLeft: "50px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Input
                        className="form-control align-end"
                        type="number"
                        style={{ padding: "5px 2px", width: "80px" }}
                        value={alternative.discount1}
                        disabled={true}
                      />
                      <span>
                        <b>%</b>
                      </span>
                    </div>
                  )}
                  {/* </div> */}

                  {/* <div className="d-flex-row"> */}
                  <FormGroup switch>
                    <Input
                      type="switch"
                      checked={alternative.applyInflation}
                      style={{
                        backgroundColor: alternative.applyInflation
                          ? "green"
                          : "transparent",
                      }}
                      disabled={true}
                    />
                    <Label check style={{ marginLeft: "10px" }}>
                      Apply Inflation Rates
                    </Label>
                  </FormGroup>
                  {alternative?.applyInflation == true && (
                    <div>
                      <Label className="form-label">Estimate Year</Label>
                      <p>
                        {alternative.yearPlanned == 0
                          ? alternative.fyStart - 1
                          : alternative.yearPlanned}
                      </p>
                    </div>
                  )}
                  {/* </div> */}

                  {(alternative?.useDiscount == true ||
                    alternative?.applyInflation == true) && (
                    <FormGroup switch>
                      <Input
                        type="switch"
                        checked={useRates}
                        style={{
                          backgroundColor: useRates ? "green" : "transparent",
                        }}
                        disabled={true}
                      />
                      <Label check style={{ marginLeft: "10px" }}>
                        View Cost Pools with Rates Applied
                      </Label>
                    </FormGroup>
                  )}
                </div>
              </div>
            </div>
          </Row>

          {scenario?.costMultiplier == 1000 && (
            <Label style={{ marginTop: "10px", marginLeft: "10px" }}>
              Enter Amounts in $000's
            </Label>
          )}

          {isLoading && costTable1.length > 0 && (
            <Loader
              type="line-scale-pulse-out"
              color={colors.primary}
              style={{ textAlign: "center" }}
            />
          )}
          <Row style={{ marginTop: "20px" }}>
            <Col md={showResourceEstimates ? 6 : 12}>
              {costPoolYears.length > 0 && (
                <table
                  className="table table-bordered lower-padding-table"
                  style={{ backgroundColor: "white", fontSize: "12px" }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "18%" }}></th>
                      {costPoolYears.map((y, idx) => (
                        <th
                          key={idx}
                          style={{ width: `${35 / costPoolYears.length}%` }}
                        >
                          FY {y.yearID}
                        </th>
                      ))}

                      <th style={{ width: "7%" }}>Total</th>
                      {!showResourceEstimates && (
                        <th style={{ width: "40%" }}>Notes</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {getTableRowsToRender(costTable1, "costTable1")}
                    {getTableRowsToRender(costTable2, "costTable2")}
                    {getTableRowsToRender(costTable3, "costTable3")}
                    <tr
                      style={{ backgroundColor: "lightgray", fontWeight: 600 }}
                    >
                      <td style={{ textAlign: "Right", fontWeight: 600 }}>
                        Total Cost
                      </td>
                      {costPoolYears.map((y, idx) => (
                        <td
                          key={idx}
                          className="align-end"
                          style={{ fontWeight: "bold" }}
                        >
                          {currencyFormatter.format(
                            getYearTotals(
                              [...costTable1, ...costTable2, ...costTable3],
                              y
                            )
                          )}
                        </td>
                      ))}
                      <td className="align-end" style={{ fontWeight: "bold" }}>
                        {currencyFormatter.format(
                          [...costTable1, ...costTable2, ...costTable3].reduce(
                            (acc, item) => acc + item.totalAmount,
                            0
                          )
                        )}
                      </td>
                      {!showResourceEstimates && <td></td>}
                    </tr>
                    {(hasBenefitRecordsTable1 ||
                      hasBenefitRecordsTable2 ||
                      hasBenefitRecordsTable3) && (
                      <tr>
                        <th style={{ width: "18%" }}></th>
                        {costPoolYears.map((y, idx) => (
                          <th
                            key={idx}
                            style={{ width: `${35 / costPoolYears.length}%` }}
                          >
                            FY {y.yearID}
                          </th>
                        ))}

                        <th style={{ width: "7%" }}>Total</th>
                        {!showResourceEstimates && (
                          <th style={{ width: "40%" }}>Notes</th>
                        )}
                      </tr>
                    )}
                    {hasBenefitRecordsTable1 &&
                      getTableRowsToRender(benefitTable1, "benefitTable1")}
                    {hasBenefitRecordsTable2 &&
                      getTableRowsToRender(benefitTable2, "benefitTable2")}
                    {hasBenefitRecordsTable3 &&
                      getTableRowsToRender(benefitTable3, "benefitTable3")}
                    {(hasBenefitRecordsTable1 ||
                      hasBenefitRecordsTable2 ||
                      hasBenefitRecordsTable3) && (
                      <tr
                        style={{
                          backgroundColor: "lightgray",
                          fontWeight: 600,
                        }}
                      >
                        <td style={{ textAlign: "right" }}>Total Benefit</td>
                        {costPoolYears.map((y, idx) => (
                          <td key={idx} className="align-end">
                            {currencyFormatter.format(
                              getYearTotals(
                                [
                                  ...benefitTable1,
                                  ...benefitTable2,
                                  ...benefitTable3,
                                ],
                                y
                              )
                            )}
                          </td>
                        ))}

                        <td
                          className="align-end"
                          style={{ fontWeight: "bold" }}
                        >
                          {currencyFormatter.format(
                            [
                              ...benefitTable1,
                              ...benefitTable2,
                              ...benefitTable3,
                            ].reduce((acc, item) => acc + item.totalAmount, 0)
                          )}
                        </td>
                        {!showResourceEstimates && <td></td>}
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
              <h3>
                <b>
                  Summary {alternative.useDiscount && " - Net Present Value"}{" "}
                  {alternative.applyInflation && " - Inflation Applied"}{" "}
                  {scenario?.costMultiplier == 1000 && "($000’s)"}
                </b>
              </h3>
              {forecastSummary.length > 0 && (
                <table
                  className="table table-bordered table-responsive lower-padding-table"
                  style={{
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr>
                      {Object.keys(forecastSummary[0])
                        .filter(x => x.startsWith("c-"))
                        .map((k, idx) => (
                          <th
                            style={{
                              textAlign:
                                parseInt(k.replace("c-", "")) !== NaN
                                  ? "center"
                                  : "start",
                            }}
                            key={idx}
                          >
                            {k.replace("c-", "") == "Cost Pool"
                              ? ""
                              : k.replace("c-", "")}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {forecastSummary.map((f, i) => {
                      return (
                        <tr
                          key={i}
                          style={{
                            backgroundColor: f.backColor,
                          }}
                        >
                          {Object.keys(f)
                            .filter(x => x.startsWith("c-"))
                            .map((k, idx) => (
                              <td
                                key={idx}
                                className={!isNaN(f[k]) ? "align-end" : ""}
                                style={{
                                  fontWeight:
                                    idx == 0 || f.bold == "1" ? 600 : 100,
                                }}
                              >
                                {idx == 0
                                  ? f[k]
                                  : f.format == "$"
                                  ? currencyFormatter.format(f[k])
                                  : f.format == "%"
                                  ? `${f[k]}%`
                                  : f[k]}
                              </td>
                            ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </Col>
            {showResourceEstimates && resourceEstimateData.length > 0 && (
              <Col md="6">
                <table
                  className="table table-bordered table-responsive lower-padding-table"
                  style={{
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "10%" }}></th>
                      {Object.keys(resourceEstimateData[0])
                        .filter(x => x.startsWith("c-"))
                        .map((k, idx) => (
                          <th key={idx}>{k.replace("c-", "")}</th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resourceEstimateData.map((r, idx) => (
                      <tr key={idx}>
                        <td></td>
                        {Object.keys(r)
                          .filter(x => x.startsWith("c-"))
                          .map((k, idx) => (
                            <td
                              key={idx}
                              className={!isNaN(r[k]) ? "align-end" : ""}
                            >
                              {!isNaN(r[k])
                                ? currencyFormatter.format(r[k])
                                : r[k]}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            )}
          </Row>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default ForecastSectionViewOnly
